import React, {
    createContext,
    useState,
    useEffect,
    useContext,
    useRef,
    useCallback,
} from 'react'
import { withRouter } from 'react-router-dom'
import debounce from 'debounce'
import deepEqual from 'deep-equal'

import { ParameterContext } from '../../../wrappers/ParameterContext'
import { APIRequestContext } from '../../../wrappers/APIRequestContext'
import { AppStateContext } from '../../explorer/AppStateContext'
import withConfig from '../../../wrappers/withConfig'
import toast from '../../../elem/Toast'

import {
    getQueryStringFromParams,
    removeOrderAndPageParamsFromOtherFields,
} from '../../../../utils/params'
import getFilterParamsFromForm from '../../../../utils/form/getFilterParamsFromForm'

const DataContext = createContext(null)

const DataContextProvider = ({ history, config, children }) => {
    const formName = 'well'
    const {
        params,
        setParams,
        storedParams,
        setStoredParams,
        refinedParams,
        setRefinedParams,
    } = useContext(ParameterContext)
    const { authenticatedFetch } = useContext(APIRequestContext)
    const {
        mapState: { selectedFeatures, mapSelectionIds },
        promotedRecords,
    } = useContext(AppStateContext)
    const [tableData, setTableData] = useState([])
    const [tableMetadata, setTableMetadata] = useState({})
    const [filterFields, setFilterFields] = useState([])
    const [filterResultsCount, setFilterResultsCount] = useState()
    const [filterResultsLoading, setFilterResultsLoading] = useState(false)
    const [loading, setLoading] = useState(true)
    const [dataAbortController, setDataAbortController] = useState(
        new AbortController()
    )
    const [filterResultsAbortController, setFilterResultsAbortController] =
        useState(new AbortController())
    const associatedParams = useRef({})
    const associatedFeatures = useRef([])
    const activeMapSelectionIds = useRef(null)
    const { API_URL, ID_COLUMN } = config

    const fetchData = useCallback(
        (params) => {
            const selectedIds = encodeURI(
                selectedFeatures.map((x) => x.get(ID_COLUMN)).toString()
            )
            setLoading(true)
            const abort = new AbortController()
            setDataAbortController(abort)
            const paramString = getQueryStringFromParams(params)
            const explorerRelatedFormDataString = JSON.stringify(
                paramString[formName]
            )
            if (checkIfHasTooManyParams(explorerRelatedFormDataString)) {
                setFilterResultsLoading(false)
                return
            }
            authenticatedFetch(`${API_URL}/well?${paramString}`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Headers':
                        'Access-Control-Allow-Origin, X-Requested-With, Content-Type, Accept',
                },
                signal: abort.signal,
                body: JSON.stringify({
                    selectedIds,
                    mapSelectionIds,
                    storedParams,
                    refinedParams,
                }),
            })
                .then(async (response) => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then((response) => {
                    const pathName = history.location.pathname
                    history.push(`${pathName}?${paramString}`)
                    setTableData(response.data)
                    setTableMetadata(response.meta)
                    setLoading(false)
                })
                .catch((e) => {
                    if (e.name !== 'AbortError') {
                        toast({
                            level: 'error',
                            message:
                                'Well List: ' +
                                (e.message
                                    ? e.message
                                    : 'Unable to connect to the server. Please try again later.'),
                        })
                        setLoading(false)
                    }
                })
        },
        [
            selectedFeatures,
            promotedRecords,
            mapSelectionIds,
            storedParams,
            refinedParams,
        ]
    )

    const fetchResultsDebounce = debounce(
        (e, formData, storedParams, refinedParams) => {
            // query for the updated count
            const explorerRelatedFormData = getFilterParamsFromForm(formData)
            const newParams = {
                ...params,
                well: {
                    ...explorerRelatedFormData,
                },
            }
            const abort = new AbortController()
            setFilterResultsAbortController(abort)
            const paramString = getQueryStringFromParams(newParams)
            if (checkIfHasTooManyParams(paramString)) {
                setFilterResultsLoading(false)
                return
            }
            authenticatedFetch(`${API_URL}/well/count?${paramString}`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Headers':
                        'Access-Control-Allow-Origin, X-Requested-With, Content-Type, Accept',
                },
                signal: abort.signal,
                body: JSON.stringify({
                    mapSelectionIds,
                    storedParams,
                    refinedParams,
                }),
            })
                .then(async (response) => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then((response) => {
                    setFilterResultsCount(response.map((x) => x.count))
                    setFilterResultsLoading(false)
                })
                .catch((e) => {
                    if (e.name !== 'AbortError') {
                        toast({
                            level: 'error',
                            message:
                                'Well List: ' +
                                (e.message
                                    ? e.message
                                    : 'Unable to connect to the server. Please try again later.'),
                        })
                        setFilterResultsLoading(false)
                    }
                })
        },
        3000,
        false
    )

    const fetchResultsCount = useCallback(
        (e, formData) => {
            filterResultsAbortController.abort()
            setFilterResultsLoading(true)
            fetchResultsDebounce.clear()
            fetchResultsDebounce(e, formData, storedParams, refinedParams)
        },
        [
            filterResultsAbortController,
            storedParams,
            refinedParams,
            selectedFeatures,
            mapSelectionIds,
        ]
    )

    const fetchResultsDebounceWithParams = debounce(
        (params, storedParams, refinedParams) => {
            // query for the updated count
            const selectedIds = encodeURI(
                selectedFeatures.map((x) => x.get(ID_COLUMN)).toString()
            )
            const abort = new AbortController()
            setFilterResultsAbortController(abort)
            const paramString = getQueryStringFromParams(params)
            const explorerRelatedFormDataString = JSON.stringify(
                paramString[formName]
            )
            if (checkIfHasTooManyParams(explorerRelatedFormDataString)) {
                setFilterResultsLoading(false)
                return
            }

            authenticatedFetch(`${API_URL}/well/count?${paramString}`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Headers':
                        'Access-Control-Allow-Origin, X-Requested-With, Content-Type, Accept',
                },
                signal: abort.signal,
                body: JSON.stringify({
                    selectedIds,
                    mapSelectionIds,
                    storedParams,
                    refinedParams,
                }),
            })
                .then(async (response) => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then((response) => {
                    setFilterResultsCount(response.map((x) => x.count))
                    setFilterResultsLoading(false)
                })
                .catch((e) => {
                    if (e.name !== 'AbortError') {
                        toast({
                            level: 'error',
                            message:
                                'Well List: ' +
                                (e.message
                                    ? e.message
                                    : 'Unable to connect to the server. Please try again later.'),
                        })
                        setFilterResultsLoading(false)
                    }
                })
        },
        3000,
        false
    )

    const fetchResultsCountWithParams = useCallback(
        (params) => {
            filterResultsAbortController.abort()
            setFilterResultsLoading(true)
            fetchResultsDebounceWithParams.clear()
            fetchResultsDebounceWithParams(params, storedParams, refinedParams)
        },
        [
            filterResultsAbortController,
            storedParams,
            refinedParams,
            selectedFeatures,
            mapSelectionIds,
        ]
    )

    const fetchFilterFields = () => {
        authenticatedFetch(`${API_URL}/well/filterFields`)
            .then(async (response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    const error = await response.text()
                    throw new Error(error)
                }
            })
            .then((response) => {
                setFilterFields(response.data)
            })
            .catch((e) => {
                toast({
                    level: 'error',
                    message:
                        'Well Filter: ' +
                        (e.message
                            ? e.message
                            : 'Unable to connect to the server. Please try again later.'),
                })
            })
    }

    const checkIfHasTooManyParams = useCallback(
        (explorerRelatedFormDataString) => {
            if (
                explorerRelatedFormDataString &&
                explorerRelatedFormDataString.length >= 2000
            ) {
                // only write to the URL if the querystring would be less than 2000 characters, most browsers will error out
                // somewhere around that queryString length
                toast({
                    level: 'error',
                    message: `The maximum number of applied filters has been exceeded. Please refine your search or download a copy of the database`,
                    alert: true,
                })
                return true
            }
            return false
        },
        [params]
    )

    // fetch data on parameter changes
    useEffect(() => {
        const newParams = removeOrderAndPageParamsFromOtherFields(
            params,
            formName
        )
        if (newParams && newParams.app) {
            delete newParams.app.samplePromoted
        }
        if (
            !deepEqual(associatedParams.current, {
                newParams,
                storedParams,
                refinedParams,
            }) ||
            (!deepEqual(associatedFeatures.current, selectedFeatures) &&
                promotedRecords['well']) ||
            !deepEqual(activeMapSelectionIds.current, mapSelectionIds)
        ) {
            dataAbortController.abort()
            fetchData(params)
            fetchResultsCountWithParams(params) //also update the filter's menu count so all counts are on sync
            associatedParams.current = {
                newParams,
                storedParams,
                refinedParams,
            }
            associatedFeatures.current = selectedFeatures
            activeMapSelectionIds.current = mapSelectionIds
        }
    }, [
        params,
        dataAbortController,
        selectedFeatures,
        promotedRecords['well'],
        mapSelectionIds,
        storedParams,
        refinedParams,
    ])

    return (
        <DataContext.Provider
            value={{
                data: tableData,
                meta: tableMetadata,
                formName,
                params,
                setParams,
                storedParams,
                setStoredParams,
                refinedParams,
                setRefinedParams,
                filterFields,
                fetchData,
                fetchFilterFields,
                fetchResultsCount,
                filterResultsCount,
                filterResultsLoading,
                loading,
            }}
        >
            {children}
        </DataContext.Provider>
    )
}

export { DataContext }
export default withRouter(withConfig(DataContextProvider))
